
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import TagSelectPopup from "@/components/TagSelectPopup/index.vue";
import TipsPopup from "@/components/tipsPopup/index.vue";
import {
  CommonTag,
  JielongEditMode,
  JielongGoodsItem,
  JielongGoodsTag,
  pickGoodsImage,
  createGoodsTag,
  deleteGoodsTag,
  fetchCommonTags,
} from "@/api/jielong";

let keySeed = 0;
function genKey (): string {
  keySeed += 1;
  return "g" + Date.now() + "-" + keySeed;
}

/** 标签名数组 → 带 sortNo 的 tagList */
function labelsToTagList (labels: string[]): JielongGoodsTag[] {
  return (labels || []).map((tagName, idx) => ({
    sortNo: idx + 1,
    tagName,
  }));
}

/** tagList → 按 sortNo 排序后的标签名数组（弹窗交互用） */
function tagListToLabels (tagList: JielongGoodsTag[]): string[] {
  return sortedTagList(tagList).map(t => t.tagName);
}

function sortedTagList (tagList: JielongGoodsTag[]): JielongGoodsTag[] {
  return [...(tagList || [])].sort((a, b) => (a.sortNo || 0) - (b.sortNo || 0));
}

/** 从选品数据中解析接龙价 */
export function pickJoinPrice (source: any): number | null {
  const raw = source?.joinPrice;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return isNaN(n) ? null : n;
}

/** 将"选择商品"页返回的商品数据（ShopGoodsInfoDto 等后端商品结构）转换为接龙商品项 */
export function createGoodsItemFromSelected (source: any, sortNo = 0): JielongGoodsItem {
  return {
    _key: genKey(),
    goodsId: source.goodsId || "",
    saleTitle: source.goodsName || "",
    tagList: [],
    joinSellUnit: Number(source.joinSellUnit) || 1,
    sellUnitWay: source.sellUnitWay || "",
    joinPrice: pickJoinPrice(source),
    stockQty: null,
    soldQty: 0,
    showStock: 0,
    minBuyQty: null,
    maxBuyQty: null,
    originalPrice: source.scribingPrice != null && source.scribingPrice !== ""
      ? Number(source.scribingPrice)
      : null,
    sortNo,
    goodsImage: pickGoodsImage(source),
    imgList: source.imgList || source.storeImageList || [],
  };
}

@Component({ components: { TagSelectPopup, TipsPopup } })
export default class JielongGoodsCard extends Vue {
  @Prop({ type: Array, default: () => [] }) value!: JielongGoodsItem[];
  @Prop({ type: Array, default: () => [] }) commonTags!: CommonTag[];
  @Prop({ type: String, default: "create" }) editMode!: JielongEditMode;

  @Getter companyId!: string;

  isSimple = false;
  localGoods: JielongGoodsItem[] = [];
  goodsErrorMsg = "";
  fieldErrors: Record<string, { saleTitle?: string; joinPrice?: string; originalPrice?: string }> = {};
  isShake = false;

  tagEditingIndex: number | null = null;
  tagPopupVisible = false;
  localCommonTags: CommonTag[] = [];

  purchaseLimitEditingIndex: number | null = null;
  purchaseLimitPopupVisible = false;
  enableMinBuyQty = false;
  minBuyQtyValue = "";
  enableMaxBuyQty = false;
  maxBuyQtyValue = "";

  tipsPopupHtmlContent = "";
  deleteIndex: number | null = null;
  private lastEmittedValue: JielongGoodsItem[] | null = null;

  @Watch("minBuyQtyValue")
  onMinBuyQtyValueChange (val: string | number): void {
    const str = String(val ?? "");
    const digits = str.replace(/\D/g, "");
    if (digits !== str) {
      this.minBuyQtyValue = digits;
    }
  }

  @Watch("maxBuyQtyValue")
  onMaxBuyQtyValueChange (val: string | number): void {
    const str = String(val ?? "");
    const digits = str.replace(/\D/g, "");
    if (digits !== str) {
      this.maxBuyQtyValue = digits;
    }
  }

  @Watch("value", { immediate: true })
  onValueChange (val: JielongGoodsItem[]): void {
    if (val === this.lastEmittedValue) {
      this.lastEmittedValue = null;
      return;
    }
    if (!val || val.length === 0) {
      this.localGoods = [];
    } else {
      this.localGoods = val.map(item => ({ ...item }));
    }
  }

  @Watch("commonTags", { immediate: true })
  onCommonTagsChange (val: CommonTag[]): void {
    // 仅在父组件传入有效列表时同步，避免默认空数组覆盖已拉取的本地标签
    if (val && val.length) {
      this.localCommonTags = [...val];
    }
  }

  created (): void {
    this.ensureCommonTags();
  }

  /** 确保本地有常用标签；打开弹窗时也可再调一次兜底 */
  ensureCommonTags (): Promise<void> {
    if (this.localCommonTags.length || this.commonTags.length) {
      if (!this.localCommonTags.length && this.commonTags.length) {
        this.localCommonTags = [...this.commonTags];
      }
      return Promise.resolve();
    }
    return fetchCommonTags(this.companyId)
      .then(tags => {
        this.localCommonTags = tags;
      })
      .catch(() => {
        /* 请求失败时保持空列表，弹窗仍可新增 */
      });
  }

  get isLimited (): boolean {
    return this.editMode === "limited";
  }

  get isCreate (): boolean {
    return this.editMode !== "limited";
  }

  get systemTagLabels (): string[] {
    return this.localCommonTags.filter(t => !t.custom).map(t => t.label);
  }

  get localCustomTags (): string[] {
    return this.localCommonTags.filter(t => t.custom).map(t => t.label);
  }

  get editingTagLabels (): string[] {
    if (this.tagEditingIndex === null) return [];
    const item = this.localGoods[this.tagEditingIndex];
    return item ? tagListToLabels(item.tagList || []) : [];
  }

  sortedTagList = sortedTagList;

  @Watch("editMode")
  onEditModeChange (): void {
    if (this.isLimited) this.isSimple = false;
  }

  toggleMode (): void {
    if (this.isLimited) return;
    this.isSimple = !this.isSimple;
  }

  onMoveUp (index: number): void {
    if (this.isLimited) return;
    if (index === 0) return;
    const arr = [...this.localGoods];
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
    this.localGoods = arr;
    this.emitChange();
  }

  onMoveDown (index: number): void {
    if (this.isLimited) return;
    if (index === this.localGoods.length - 1) return;
    const arr = [...this.localGoods];
    [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
    this.localGoods = arr;
    this.emitChange();
  }

  onMoveTop (index: number): void {
    if (this.isLimited) return;
    if (index === 0) return;
    const arr = [...this.localGoods];
    const [item] = arr.splice(index, 1);
    arr.unshift(item);
    this.localGoods = arr;
    this.emitChange();
  }

  onDelete (index: number): void {
    if (this.isLimited) return;
    this.deleteIndex = index;
    this.tipsPopupHtmlContent = `
      <P>确定要删除吗？</P>
    `;
    (this.$refs.tipsPopupRef as any).showPopup();
  }

  onDeleteConfirm (): void {
    if (this.deleteIndex === null) return;
    this.localGoods.splice(this.deleteIndex, 1);
    this.deleteIndex = null;
    this.emitChange();
  }

  onStockQtyInput (index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    // 仅允许整数；空值表示不限；有值时须 >= 1
    const digits = String(target.value ?? "").replace(/\D/g, "");
    if (digits === "") {
      this.$set(this.localGoods[index], "stockQty", null);
    } else {
      let val = parseInt(digits, 10);
      if (isNaN(val) || val < 1) val = 1;
      this.$set(this.localGoods[index], "stockQty", val);
    }
    this.emitChange();
  }

  onShowStockToggle (index: number, checked: boolean): void {
    this.$set(this.localGoods[index], "showStock", checked ? 1 : 0);
    this.emitChange();
  }

  onOriginalPriceInput (index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.value === "") {
      this.$set(this.localGoods[index], "originalPrice", null);
    } else {
      const val = Number(target.value);
      this.$set(this.localGoods[index], "originalPrice", Number.isNaN(val) ? null : val);
    }
    this.emitChange();
  }

  onSaleTitleInput (index: number, event: Event): void {
    if (!this.isCreate) return;
    const target = event.target as HTMLInputElement;
    this.$set(this.localGoods[index], "saleTitle", target.value);
    this.emitChange();
  }

  onJoinPriceInput (index: number, event: Event): void {
    if (!this.isCreate) return;
    const target = event.target as HTMLInputElement;
    if (target.value === "") {
      this.$set(this.localGoods[index], "joinPrice", null);
    } else {
      const val = Number(target.value);
      this.$set(this.localGoods[index], "joinPrice", Number.isNaN(val) ? null : val);
    }
    this.emitChange();
  }

  onEditTags (index: number): void {
    if (this.isLimited) return;
    this.tagEditingIndex = index;
    this.tagPopupVisible = true;
    this.ensureCommonTags();
  }

  onTagSave (tags: string[]): void {
    if (this.tagEditingIndex === null) return;
    this.$set(this.localGoods[this.tagEditingIndex], "tagList", labelsToTagList(tags));
    this.emitChange();
    this.tagEditingIndex = null;
  }

  async doAddTag (label: string): Promise<boolean> {
    try {
      await createGoodsTag({ storeId: this.companyId, tagName: label });
      this.localCommonTags = await fetchCommonTags(this.companyId);
      return true;
    } catch {
      return false;
    }
  }

  async doDeleteTag (label: string): Promise<boolean> {
    try {
      const found = this.localCommonTags.find(t => t.label === label);
      if (!found || !found.tagId) return false;
      await deleteGoodsTag({ storeId: this.companyId, tagId: found.tagId });
      this.localCommonTags = this.localCommonTags.filter(t => t.label !== label);
      return true;
    } catch {
      return false;
    }
  }

  onEditPurchaseLimit (index: number): void {
    const item = this.localGoods[index];
    this.purchaseLimitEditingIndex = index;
    this.enableMinBuyQty = item.minBuyQty != null;
    this.minBuyQtyValue = this.enableMinBuyQty ? String(item.minBuyQty) : "";
    this.enableMaxBuyQty = item.maxBuyQty != null;
    this.maxBuyQtyValue = this.enableMaxBuyQty ? String(item.maxBuyQty) : "";
    this.purchaseLimitPopupVisible = true;
  }

  onEnableMinBuyQtyChange (checked: boolean): void {
    if (!checked) {
      this.minBuyQtyValue = "";
    }
  }

  onEnableMaxBuyQtyChange (checked: boolean): void {
    if (!checked) {
      this.maxBuyQtyValue = "";
    }
  }

  onPurchaseLimitCancel (): void {
    this.purchaseLimitPopupVisible = false;
    this.purchaseLimitEditingIndex = null;
  }

  onMinBuyQtyStep (diff: number): void {
    if (!this.enableMinBuyQty) return;
    const current = parseInt(this.minBuyQtyValue, 10) || 0;
    this.minBuyQtyValue = String(Math.max(1, current + diff));
  }

  onMaxBuyQtyStep (diff: number): void {
    if (!this.enableMaxBuyQty) return;
    const current = parseInt(this.maxBuyQtyValue, 10) || 0;
    this.maxBuyQtyValue = String(Math.max(1, current + diff));
  }

  onPurchaseLimitSave (): void {
    if (this.purchaseLimitEditingIndex === null) return;
    const index = this.purchaseLimitEditingIndex;
    const minVal = parseInt(this.minBuyQtyValue, 10);
    const maxVal = parseInt(this.maxBuyQtyValue, 10);

    if (this.enableMinBuyQty && (!minVal || minVal < 1)) {
      this.$toast("请输入正确的起购数量");
      return;
    }
    if (this.enableMaxBuyQty && (!maxVal || maxVal < 1)) {
      this.$toast("请输入正确的每人限购数量");
      return;
    }
    if (this.enableMinBuyQty && this.enableMaxBuyQty && minVal > maxVal) {
      this.$toast("每人限购数量需大于等于起购数量");
      return;
    }

    this.$set(this.localGoods, index, {
      ...this.localGoods[index],
      minBuyQty: this.enableMinBuyQty ? minVal : null,
      maxBuyQty: this.enableMaxBuyQty ? maxVal : null,
    });
    this.emitChange();
    this.purchaseLimitPopupVisible = false;
    this.purchaseLimitEditingIndex = null;
  }

  hasPurchaseLimit (item: JielongGoodsItem): boolean {
    return item.minBuyQty != null || item.maxBuyQty != null;
  }

  onSelectGoods (): void {
    if (this.isLimited) return;
    // 进入选择商品页时回显已选商品：写入 goodsId 列表供 SelectGoods activated 读取
    const preselected = this.localGoods
      .filter(item => item.goodsId)
      .map(item => ({ goodsId: item.goodsId }));
    window.localStorage.setItem("jielongPreselectedGoods", JSON.stringify(preselected));
    this.$router.push({ name: "SelectGoods" });
  }

  validate (): boolean {
    if (this.isLimited) return true;

    this.goodsErrorMsg = "";
    this.fieldErrors = {};

    if (!this.localGoods.length) {
      this.goodsErrorMsg = "请选择接龙商品";
      this.shake();
      return false;
    }

    let ok = true;
    const errors: Record<string, { saleTitle?: string; joinPrice?: string; originalPrice?: string }> = {};
    this.localGoods.forEach((item) => {
      const itemErr: { saleTitle?: string; joinPrice?: string; originalPrice?: string } = {};
      if (!item.saleTitle || !String(item.saleTitle).trim()) {
        itemErr.saleTitle = "请填写销售标题";
        ok = false;
      }
      if (item.joinPrice == null || Number.isNaN(Number(item.joinPrice))) {
        itemErr.joinPrice = "请填写接龙价";
        ok = false;
      }
      // 划线价选填；填写时必须高于接龙价
      const originalRaw = item.originalPrice;
      if (
        originalRaw != null &&
        String(originalRaw).trim() !== "" &&
        !Number.isNaN(Number(originalRaw))
      ) {
        const joinPrice = Number(item.joinPrice);
        const originalPrice = Number(originalRaw);
        if (!Number.isNaN(joinPrice) && originalPrice <= joinPrice) {
          itemErr.originalPrice = "划线价需高于接龙价";
          ok = false;
        }
      }
      if (itemErr.saleTitle || itemErr.joinPrice || itemErr.originalPrice) {
        errors[item._key] = itemErr;
      }
    });
    this.fieldErrors = errors;
    if (!ok) {
      // 字段错误在正常模式才可见，简易模式切回正常便于看到提示
      this.isSimple = false;
      this.shake();
    }
    return ok;
  }

  shake (): void {
    this.isShake = true;
    window.setTimeout(() => {
      this.isShake = false;
    }, 1000);
  }

  formatPurchaseLimit (item: JielongGoodsItem): string {
    const parts: string[] = [];
    if (item.minBuyQty != null) {
      parts.push(`${item.minBuyQty}件起购`);
    }
    if (item.maxBuyQty != null) {
      parts.push(`限购${item.maxBuyQty}件`);
    }
    return parts.length ? parts.join("、") : "未设置默认不限";
  }

  private emitChange (): void {
    if (this.goodsErrorMsg && this.localGoods.length) this.goodsErrorMsg = "";
    if (Object.keys(this.fieldErrors).length) this.fieldErrors = {};
    const next = this.localGoods.map(item => ({ ...item }));
    this.lastEmittedValue = next;
    this.$emit("input", next);
  }
}
